import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Chip,
    Box,
    Typography,
    Stack,
} from "@mui/material";
import { useSelector } from "react-redux";
import { AxiosResponse, AxiosError } from "axios";
import EnhancedAxios from "../../axios/EnhancedAxios";
import { errorAlert } from "../../utils/alerts/alerts";
import PaginationFilter from "../filter/PaginationFilter";
import ThreeDotsLoader from "../loader/ThreeDotsLoader";

interface GiftUsernamesModalProps {
    open: boolean;
    onClose: () => void;
    giftId: number | string;
}

const GiftUsernamesModal = ({
    open,
    onClose,
    giftId,
}: GiftUsernamesModalProps) => {
    const userToken = useSelector((state: any) => state.user.userToken);
    const [currentPage, setCurrentPage] = useState(1);
    const [usernames, setUsernames] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [totalItems, setTotalItems] = useState(0);
    const itemsPerPage = 25;

    // Reset to page 1 when modal opens/closes
    useEffect(() => {
        if (open) {
            setCurrentPage(1);
        }
    }, [open]);

    // Fetch usernames from API
    useEffect(() => {
        if (open && giftId) {
            setLoading(true);
            EnhancedAxios(null, userToken)
                .get(
                    `/admin/gifts/${giftId}/usernames?page=${currentPage}&page_size=${itemsPerPage}`
                )
                .then((res: AxiosResponse) => {
                    setUsernames(res.data.usernames || []);
                    setTotalItems(res.data.pagination?.total || 0);
                    setLoading(false);
                })
                .catch((error: AxiosError) => {
                    errorAlert(error);
                    setLoading(false);
                });
        }
    }, [open, giftId, currentPage, userToken]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            sx={{
                "& .MuiPaper-root": {
                    minWidth: { xs: "290px", sm: "600px" },
                    maxHeight: "80vh",
                },
            }}
        >
            <DialogTitle>
                <Typography variant="h6" component="div">
                    أسماء الحسابات {!loading && `(${totalItems})`}
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {loading ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                // alignItems: "center",
                                minHeight: "200px",
                            }}
                        >
                            <ThreeDotsLoader />
                        </Box>
                    ) : usernames.length > 0 ? (
                        <>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
                                    gap: 1,
                                    // minHeight: "200px",
                                }}
                            >
                                {usernames.map((username, index) => (
                                    <Chip
                                        key={index}
                                        label={username}
                                        variant="outlined"
                                        color="primary"
                                        size="small"
                                    />
                                ))}
                            </Box>

                            {totalItems > itemsPerPage && (
                                <PaginationFilter
                                    totalItems={totalItems}
                                    itemsPerPage={itemsPerPage}
                                    currentPage={currentPage}
                                    setCurrentPage={setCurrentPage}
                                />
                            )}
                        </>
                    ) : (
                        <Box sx={{ textAlign: "center", py: 4 }}>
                            <Typography variant="body1" color="text.secondary">
                                لا توجد أسماء حسابات متاحة
                            </Typography>
                        </Box>
                    )}
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

export default GiftUsernamesModal;

